import { computed, useContext } from "@nuxtjs/composition-api";
import { sharedRef, useVSFContext, Logger } from "@vue-storefront/core";
import { useSapcc } from "~/composables";
import { userCheckoutStore } from "~/stores/checkout-store";

interface ErrorState {
  auth: Error | null;
  api: Error | null;
}

const useAdyen = (id: string) => {
  // Loads context used to call API endpoint
  const context = useVSFContext();
  const { i18n } = useContext();
  const appKey = i18n.localeProperties.appKey;
  const country = (i18n.localeProperties.baseStoreCode).toUpperCase();
  const merchantAccount = i18n.localeProperties.merchantAccount;
  const curLocale = i18n.localeProperties.iso;
  // Shared ref holding the response from the API
  const result = sharedRef<any>(null, `auth-${id}`);

  // Shared ref indicating whether any method is waiting for the data from the API
  const loading = sharedRef<boolean>(false, `auth-loading-${id}`);

  // Shared ref holding errors from the methods
  const error = sharedRef<ErrorState>(
    { auth: null, api: null },
    `auth-error-${id}`
  );

  // handle initial call
  const availablePaymentMethods = sharedRef<any>(null, `avlpm-${id}`);

  const { createOrder: sapccCreateOrder, result: sapccOrder } =
    useSapcc("create-sapcc-order");

  const useCheckoutStore = userCheckoutStore();
  // Method to call an API endpoint and update `result`, `loading` and `error` properties
  const load = async () => {
    Logger.debug(`useAdyen/${id}/load`);
    try {
      loading.value = true;
      const params = {
        merchantAccount,
        curLocale,
        country
      };
     let paymentMethodsRetrieved = await context.$vsf.$adyen.api.auth(params);
      const prioritizeItemWithBrands = (arr) => {
       const itemWithBrands = arr.find((item) => item.brands);

       if (itemWithBrands) {
         // Move the item with "brands" property to the second position
         const itemIndex = arr.indexOf(itemWithBrands);
         arr.splice(1, 0, arr.splice(itemIndex, 1)[0]);
       }
        return arr;
     };

     if (paymentMethodsRetrieved?.paymentMethods?.length > 0) {
      const restrictedCountries = ["PL", "DK"];
      const restrictedPaymentTypes = ["klarna", "klarna_account"];

      if (restrictedCountries.includes(country)) {
        paymentMethodsRetrieved.paymentMethods = paymentMethodsRetrieved.paymentMethods.filter(item => !restrictedPaymentTypes.includes(item.type));
      }
       paymentMethodsRetrieved.paymentMethods = prioritizeItemWithBrands(
         paymentMethodsRetrieved.paymentMethods
       );
     }

    availablePaymentMethods.value = paymentMethodsRetrieved;
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.auth}`);
    } finally {
      loading.value = false;
    }
  };

  const createOrder = async (params) => {
    Logger.debug(`useAdyen/${id}/createAdyenOrder`);
    try {
      loading.value = true;
      params.merchantAccount = merchantAccount;
      result.value = await context.$vsf.$adyen.api.createOrder(params);
      useCheckoutStore.setAdyenOrderInfo({
        appKey,
        orderObj: {
          orderId: sapccOrder?.value?.code,
          orderData: result?.value?.orderData,
          orderInfo: sapccOrder?.value,
          pspRef: result?.value?.pspReference,
          chosenPaymentMethod: params.chosenPaymentMethod,
        },
      });
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.auth}`);
    } finally {
      loading.value = false;
    }
  };

  const cancelAdyenOrder = async (params) => {
    Logger.debug(`useAdyen/${id}/cancelAdyenOrder`);
    try {
      loading.value = true;
      params.merchantAccount = merchantAccount;
      result.value = await context.$vsf.$adyen.api.cancelAdyenOrder(
        params
      );
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.auth}`);
    } finally {
      loading.value = false;
    }
  };

  const initPayment = async (params) => {
    Logger.debug(`useAdyen/${id}/initPayment`);
    // const appKey = "fiskarsmoomin";
    const orderGuid = computed(() => context.$cookies.get(`${appKey}#oi`));
    const tempOrderId = computed(() =>
      context.$cookies.get(`${appKey}#toi`)
    );
    const tempOrderCode = computed(() =>
      context.$cookies.get(`${appKey}#toc`)
    );
    try {
      loading.value = true;
      // create order on sapcc if order not yet created
      await sapccCreateOrder();

      if (!orderGuid.value) {
        //create order on Adyen
        await createOrder({
          orderCode: sapccOrder?.value?.code,
          amount: sapccOrder?.value?.totalPriceWithTax?.value,
          currency:
            sapccOrder?.value?.totalPriceWithTax?.currencyIso == "KR"
              ? "NOK"
              : sapccOrder?.value?.totalPriceWithTax?.currencyIso,
          chosenPaymentMethod: params.activePaymentMethod,
        });
        context.$cookies.set(`${appKey}#oi`, tempOrderId.value, {
          maxAge: 60 * 60 * 24 * 15,
          path: "/",
        });
        context.$cookies.set(`${appKey}#oc`, tempOrderCode.value, {
          maxAge: 60 * 60 * 24 * 15,
          path: "/",
        });
        context.$cookies.remove(`${appKey}#toi`);
        context.$cookies.remove(`${appKey}#toc`);
      }
      params.merchantAccount = merchantAccount;
      params.country = country;
      params.curLocale = curLocale;
      result.value = await context.$vsf.$adyen.api.initPayment(
        params,
        sapccOrder,
      );
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.auth}`);
    } finally {
      loading.value = false;
    }
  };

  const getPaymentStatus = async (params) => {
    Logger.debug(`useAdyen/${id}/getPaymentStatus`);
    try {
      loading.value = true;
      result.value = await context.$vsf.$adyen.api.getPaymentStatus(
        params
      );
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.getPaymentStatus}`);
    } finally {
      loading.value = false;
    }
  };

  return {
    load,
    initPayment,
    getPaymentStatus,
    createOrder,
    cancelAdyenOrder,
    availablePaymentMethods: computed(() => availablePaymentMethods.value),
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };
};

export default useAdyen;
